/**
 * WhatsApp click-to-send (no API). Builds a wa.me link from the tenant's dial
 * code + the customer's phone and a rendered message template, then opens it —
 * the staff user taps Send inside WhatsApp. Config (dial code + templates)
 * comes from `/whatsapp/config`.
 */
import type { WhatsAppConfig, WhatsAppConfigUpdate } from '@erp/shared';

import { api } from './api.js';

export type { WhatsAppConfig } from '@erp/shared';
export { WHATSAPP_DOC_TYPES, type WhatsAppDocType } from '@erp/shared';

export async function getWhatsAppConfig(): Promise<WhatsAppConfig> {
  return (await api().get<WhatsAppConfig>('/whatsapp/config')).data;
}

export async function updateWhatsAppConfig(input: WhatsAppConfigUpdate): Promise<WhatsAppConfig> {
  const cfg = (await api().patch<WhatsAppConfig>('/whatsapp/config', input)).data;
  cache = cfg;
  return cfg;
}

// Small cache so the Send button doesn't refetch config on every click.
let cache: WhatsAppConfig | null = null;
let inflight: Promise<WhatsAppConfig> | null = null;
export async function getWhatsAppConfigCached(): Promise<WhatsAppConfig> {
  if (cache) return cache;
  if (!inflight) inflight = getWhatsAppConfig().then((c) => { cache = c; inflight = null; return c; });
  return inflight;
}
export function clearWhatsAppConfigCache(): void { cache = null; inflight = null; }

/**
 * Normalize a phone to wa.me digits (no '+', no separators). Local numbers get
 * the tenant dial code; a leading 0 (trunk prefix) is replaced by it; numbers
 * already starting with the dial code (or 00…) are passed through.
 */
export function normalizeWhatsappPhone(phone: string | null | undefined, dialCode: string): string | null {
  if (!phone) return null;
  let digits = phone.replace(/\D/g, '');
  if (!digits) return null;
  const dc = (dialCode || '').replace(/\D/g, '');
  if (digits.startsWith('00')) digits = digits.slice(2);
  else if (digits.startsWith('0')) digits = dc + digits.slice(1);
  else if (dc && !digits.startsWith(dc)) digits = dc + digits;
  return digits.length >= 6 ? digits : null;
}

/** Substitute {placeholders} with values (missing → empty). */
export function renderTemplate(template: string, vars: Record<string, string | undefined>): string {
  return template.replace(/\{(\w+)\}/g, (_m, k: string) => vars[k] ?? '');
}

/**
 * The text actually SENT: the template rendered, plus the public document link
 * appended when the template carries no `{link}` of its own.
 *
 * The fallback is not belt-and-braces, it is the only thing that gets the link
 * to a real tenant. `WhatsAppService.setConfig` persists all six templates as
 * one blob the first time Settings → WhatsApp is saved, so a shop that saved
 * before a template gained `{link}` (Duhok edited theirs through the UI) keeps
 * the link-less string forever and NO change to the server-side defaults can
 * ever reach it — the customer would get an order total with nothing to open.
 *
 * Keyed on the caller passing a `link`, not on the doc type: the templates with
 * nothing to link to (payment, POS receipt, general message) never pass one and
 * are left exactly as written.
 */
export function renderWhatsAppMessage(template: string, vars: Record<string, string | undefined>): string {
  const link = vars.link?.trim() ?? '';
  const hasSlot = template.includes('{link}');
  // Nothing to link to (the mint failed, or this surface never mints one): drop
  // the whole LINE the link sat on rather than sending a dangling "👉 View your
  // order:" with nothing after the colon.
  const body = !link && hasSlot ? template.replace(/^.*\{link\}.*(?:\r?\n|$)/gm, '') : template;
  const text = renderTemplate(body, vars).trim();
  if (!link || hasSlot) return text;
  return text ? `${text}\n${link}` : link;
}

/** Open WhatsApp (web or app) with a pre-filled message. */
export function openWhatsApp(phoneDigits: string, text: string): void {
  const url = `https://wa.me/${phoneDigits}?text=${encodeURIComponent(text)}`;
  window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * Send the message server-side via the tenant's configured WhatsApp gateway
 * (Settings → OTP — Evolution / Green API), so it goes out from the store's own
 * number. Rejects with the axios error — cause code `whatsapp.unconfigured` when
 * no gateway is set (the caller then falls back to a wa.me link).
 */
export async function sendWhatsAppMessage(to: string, message: string): Promise<void> {
  await api().post('/whatsapp/send', { to, message });
}
